import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type WwwBlogStatus = 'draft' | 'published' | 'archived';

export type WwwAdminBlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  content: string;
  coverImageUrl?: string | null;
  tags: string[];
  status: WwwBlogStatus;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateWwwAdminBlogPostInput = {
  slug: string;
  title: string;
  excerpt?: string;
  content: string;
  coverImageUrl?: string;
  tags?: string[];
  status?: WwwBlogStatus;
  publishedAt?: string;
};

export type UpdateWwwAdminBlogPostInput = Partial<CreateWwwAdminBlogPostInput>;

async function parseError(response: Response, fallbackMessage: string) {
  const text = await response.text().catch(() => '');
  if (!text) return fallbackMessage;
  try {
    const json = JSON.parse(text) as { message?: string; detail?: string };
    return json.message ?? json.detail ?? fallbackMessage;
  } catch {
    return text;
  }
}

export async function fetchWwwAdminBlogPosts(): Promise<WwwAdminBlogPost[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load WWW blog posts.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/blog`);
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to load WWW blog posts (${response.status})`));
  }
  const payload = (await response.json()) as { posts?: WwwAdminBlogPost[] };
  return payload.posts ?? [];
}

export async function createWwwAdminBlogPost(input: CreateWwwAdminBlogPostInput): Promise<WwwAdminBlogPost> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to create WWW blog posts.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/blog`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to create WWW blog post (${response.status})`));
  }
  return (await response.json()) as WwwAdminBlogPost;
}

export async function updateWwwAdminBlogPost(
  id: string,
  input: UpdateWwwAdminBlogPostInput,
): Promise<WwwAdminBlogPost> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update WWW blog posts.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/blog/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to update WWW blog post (${response.status})`));
  }
  return (await response.json()) as WwwAdminBlogPost;
}

export async function deleteWwwAdminBlogPost(id: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete WWW blog posts.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/blog/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to delete WWW blog post (${response.status})`));
  }
}

